import type { ScreeningProgram } from "./hooks"
import { assign, setup } from "xstate"

// Stashed on DISQUALIFIED so NotEligiblePage can show the right copy.
export type ScreeningExclusion = { exclusionMessageId: string; emergency: boolean }

export type BookingFlowInput = { alreadySignedConsent: boolean; program: ScreeningProgram }
export type BookingFlowContext = {
  alreadySignedConsent: boolean
  program: ScreeningProgram
  exclusion: ScreeningExclusion | null
}

export type BookingFlowEvent = { type: "CONTINUE" } | { type: "DISQUALIFIED"; exclusion: ScreeningExclusion }

export const bookingFlowMachine = setup({
  types: {
    input: {} as BookingFlowInput,
    context: {} as BookingFlowContext,
    events: {} as BookingFlowEvent
  },
  guards: {
    // Skip the consent page when the member has already signed the current required
    // consents — fed in as machine input from getConsentStatus.
    alreadyConsented: ({ context }) => context.alreadySignedConsent,
    // Exit guard, still stubbed off until wired to real data.
    hasNoAvailability: () => false // TODO: derive from the availability lookup
  },
  actions: {
    recordExclusion: assign({
      exclusion: ({ event }) => (event.type === "DISQUALIFIED" ? event.exclusion : null)
    })
  }
}).createMachine({
  id: "bookingFlow",
  // Provider finder launches the flow, so the machine itself starts at Consent.
  initial: "consent",
  context: ({ input }) => ({
    alreadySignedConsent: input.alreadySignedConsent,
    program: input.program,
    exclusion: null
  }),
  states: {
    consent: {
      always: [{ target: "healthQuestions", guard: "alreadyConsented" }],
      on: { CONTINUE: "healthQuestions" }
    },

    // Nested so it can grow into multiple (possibly member-data-driven) views without touching
    // the outer states. The page sends DISQUALIFIED on an ineligible result, else CONTINUE.
    healthQuestions: {
      initial: "view",
      states: {
        view: {
          on: {
            CONTINUE: { target: "done" },
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "seeAvailability" }
    },

    // One page whose form reveals preferences -> date -> time as the member submits;
    // will later host the inner booking machine as a child.
    seeAvailability: {
      on: {
        CONTINUE: [{ target: "noAvailability", guard: "hasNoAvailability" }, { target: "booked" }]
      }
    },

    booked: { type: "final" },
    notEligible: { id: "notEligible", type: "final" },
    noAvailability: { type: "final" }
  }
})
